/* eslint-disable no-invalid-this */

import ApiService, { ACCOUNTS_API_CONTEXT, ACCOUNTS_API_HOST } from './ApiService';
import { download } from './helpers';

/**
 * @override
 */
class AccountsService extends ApiService {
  /**
   * constructor
   */
  constructor() {
    super({ baseURL: ACCOUNTS_API_HOST });
  }
  /**
   * Get group list
   * @param {Object} sort Sort
   * @param {Object} config Axios config
   * @return {Object} Response with list of accounts
   */
  getAccounts = async (sort, config) => {
    if (sort.trim() !== '') {
      return this.getList(`${ACCOUNTS_API_CONTEXT}/accounts?$orderby=${sort}`, config);
    } else {
      return this.getList(`${ACCOUNTS_API_CONTEXT}/accounts`, config);
    }
  };

  /**
   * Get CNMV Data from DDBB
   * @param {Object} config configuration object
   * @return {Object} Response with cnmv data
   */
  getCnmvData = config => {
    return this.getList(`${ACCOUNTS_API_CONTEXT}/cnmv`, config);
  };

  /**
   * List pension plans
   * @param {Object} config configuration object
   * @return {Object} Response with pension plans data
   */
  listPensionPlansData = config => {
    return this.getList(`${ACCOUNTS_API_CONTEXT}/pension-plans`, config);
  };

  /**
   * Get interveners in file
   * @param {String} mimetype mime type
   * @param {String} filename Filename
   * @param {Object} config Axios config
   * @return {Object} Response with account
   */
  downloadAccounts = (mimetype, filename = null, config = {}) => {
    Object.assign(config || {}, { headers: { Accept: mimetype } });
    return this.get(`${ACCOUNTS_API_CONTEXT}/accounts`, config).then(response => download(response, filename));
  };

  /**
   * Get json account
   * @param {String} uuid Account UUID
   * @param {Object} config Axios config
   * @return {Object} Response with account
   */
  getAccountsByUUId = (uuid, config) => {
    return this.get(`${ACCOUNTS_API_CONTEXT}/accounts/${uuid}`, config);
  };

  /**
   * Get account in file
   * @param {String} uuid Account UUID
   * @param {String} mimetype mime type
   * @param {String} filename Filename
   * @param {Object} config Axios config
   * @return {Object} Response with account
   */
  downloadAccountsByUUId = (uuid, mimetype, filename = null, config = {}) => {
    Object.assign(config || {}, { headers: { Accept: mimetype } });
    return this.get(`${ACCOUNTS_API_CONTEXT}/accounts/${uuid}`, config).then(response => download(response, filename));
  };

  /**
   * @param {String} url Url
   * @param {Object} config Axios config
   * @return {Object} Response with list of accounts
   */
  getPagination = (url, config) => this.get(`${url}`, config);
  /**
   * Buscar cuentas
   * @param {Array} search Búsqueda
   * @param {Object} config Axios config
   * @param {Object} sort Sort
   * @return {Object} Response with accounts
   */
  getSearch = (search, config, sort = false) => {
    const sortQueryParam = sort ? `&$orderby=${sort}` : '';
    return this.getList(`${ACCOUNTS_API_CONTEXT}/accounts?$search=${search}${sortQueryParam}`, config);
  };
  /**
   * Get accounts Documents
   * @param {String} uuid Account UUID
   * @param {Object} config Axios config
   * @return {Object} Response with account documents data
   */
  getAccountDocuments = (uuid, config) => {
    return this.getList(`${ACCOUNTS_API_CONTEXT}/accounts/${uuid}/documents`, config);
  };
  /**
   * Get accounts Documents Validations
   * @param {String} uuid Account UUID
   * @param {Object} config Axios config
   * @return {Object} Response with account documents validations data
   */
  getAccountDocumentsValidations = (uuid, config) => {
    return this.getList(`${ACCOUNTS_API_CONTEXT}/accounts/${uuid}/document-validations`, config);
  };

  /**
   * Get categories document
   * @param {String} uuid Account UUID
   * @param {Object} config Axios config
   * @return {Object} Response with account categories data
   */
  getAccountCategories = (uuid, config) => {
    return this.getList(`${ACCOUNTS_API_CONTEXT}/accounts/${uuid}/document-categories`, config);
  };

  /**
   * @override
   */
  getOperationCategories = (uuid, config) => {
    return this.getList(`${ACCOUNTS_API_CONTEXT}/operations/${uuid}/document-categories`, config);
  };

  /**
   * Get accounts historic
   * @param {String} uuid Account UUID
   * @param {Object} config Axios config
   * @return {Object} Response with account historic data
   */
  getAccountHistoric = (uuid, config) => {
    return this.getList(`${ACCOUNTS_API_CONTEXT}/accounts/${uuid}/historical`, config);
  };
  /**
   * Get account Operations
   * @param {String} uuid Account UUID
   * @param {Object} config Axios config
   * @return {Object} Response with account operations data
   */
  getAccountOperations = (uuid, config) => {
    return this.getList(`${ACCOUNTS_API_CONTEXT}/accounts/${uuid}/operations`, config);
  };
  /**
   * Get account bank accounts
   * @param {String} uuid Account UUID
   * @param {Object} config Axios config
   * @return {Object} Response with account bank accounts
   */
  getAccountBankAccounts = (uuid, config) => {
    return this.getList(`${ACCOUNTS_API_CONTEXT}/accounts/${uuid}/bank-accounts`, config);
  };
  /**
   * Delete bank account
   * @param {String} accountUuid Account UUID
   * @param {String} bankAccountUuid Bank account UUID
   * @return {Object} Response of deletion
   */
  deleteBankAccount = (accountUuid, bankAccountUuid) => {
    return this.delete(`${ACCOUNTS_API_CONTEXT}/accounts/${accountUuid}/bank-accounts/${bankAccountUuid}`);
  };
  /**
   * Create bank account
   * @param {String} accountUuid Account UUID
   * @param {Object} bankAccountData Bank account data
   * @return {Object} Response of creation
   */
  createBankAccount = (accountUuid, bankAccountData) => {
    return this.post(`${ACCOUNTS_API_CONTEXT}/accounts/${accountUuid}/bank-accounts`, bankAccountData);
  };
  /**
   * Update bank account
   * @param {String} accountUuid Account UUID
   * @param {Object} updatedBankAccountData Updated bank account data
   * @return {Object} Response of patch
   */
  updateBankAccount = (accountUuid, updatedBankAccountData) => {
    return this.patch(
      `${ACCOUNTS_API_CONTEXT}/accounts/${accountUuid}/bank-accounts/${updatedBankAccountData.uuid}`,
      updatedBankAccountData
    );
  };
  /**
   * Get account Interveners
   * @param {String} uuid Account UUID
   * @param {Object} config Axios config
   * @return {Object} Response with account interveners data
   */
  getAccountInterveners = (uuid, config) => {
    return this.getList(`${ACCOUNTS_API_CONTEXT}/accounts/${uuid}/interveners`, config);
  };
  updateAccountInterveners = (uuidAccount, uuidIntervener, config) => {
    return this.patch(`${ACCOUNTS_API_CONTEXT}/accounts/${uuidAccount}/interveners/${uuidIntervener}`, config);
  };
  deleteAccountInterveners = (uuidAccount, uuidIntervener, config) => {
    return this.delete(`${ACCOUNTS_API_CONTEXT}/accounts/${uuidAccount}/interveners/${uuidIntervener}`, config);
  };
  /**
   * Get account Issues
   * @param {String} uuid account UUID
   * @param {Object} config Axios config
   * @return {Object} Response with account issues data
   */
  getAccountIssues = (uuid, config) => {
    return this.getList(`${ACCOUNTS_API_CONTEXT}/accounts/${uuid}/issues`, config);
  };
  /**
   * Get account tests
   * @param {String} uuid account UUID
   * @param {Object} config Axios config
   * @return {Object} Response with account tests data
   */
  getAccountTests = (uuid, config) => {
    return this.getList(`${ACCOUNTS_API_CONTEXT}/accounts/${uuid}/mifid`, config);
  };

  /**
   * @override
   */
  upload = payload => {
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    };

    const formData = new FormData();
    for (let i = 0; i < payload.files.length; i++) {
      formData.append('attachment', payload.files[i]);
      formData.append('filename', payload.filesName[i]);
      formData.append('category', typeof payload.category === 'string' ? payload.category : payload.category[i]);
      formData.append(
        'subcategory',
        typeof payload.subcategory === 'string' ? payload.subcategory : payload.subcategory[i]
      );
      formData.append('validated', payload.validated);
      formData.append('related_intervener', payload.related_intervener);
      if (payload.uuid_bank_account) {
        formData.append('uuid_bank_account', payload.uuid_bank_account);
      }
    }
    return this.post(`${ACCOUNTS_API_CONTEXT}/accounts/${payload.uuid}/documents`, formData, config);
  };

  /**
   * @override
   */
  download = (sort, config, headers) => {
    if (sort.trim() !== '') {
      return this.getDownload(`${ACCOUNTS_API_CONTEXT}/accounts/download?$orderby=${sort}`, config, { headers });
    } else {
      return this.getDownload(`${ACCOUNTS_API_CONTEXT}/accounts/download`, config, { headers });
    }
  };

  /**
   * @override
   */
  create = payload => {
    return this.post(`${ACCOUNTS_API_CONTEXT}/accounts`, payload);
  };

  /**
   * @override
   */
  createBulk = (uuid_account, payload) => {
    return this.post(`${ACCOUNTS_API_CONTEXT}/accounts/${uuid_account}/interveners/bulk`, payload);
  };
  createContributors = (uuid_account, payload) => {
    return this.post(`/accounts-api/accounts/${uuid_account}/contributors`, payload);
  };
  getAccountContriubutors = uuid_account => {
    return this.get(`/accounts-api/accounts/${uuid_account}/contributors`);
  };
  /**
   * @override
   */
  getDocument = (uuid, uuidDocument) => {
    return this.get(`${ACCOUNTS_API_CONTEXT}/accounts/${uuid}/documents/${uuidDocument}`, {
      responseType: 'blob'
    }).then(res => res.data);
  };

  /**
   * @override
   */
  downloadDocument = (uuid, uuidDocument) => {
    return this.get(`${ACCOUNTS_API_CONTEXT}/accounts/${uuid}/documents/${uuidDocument}`);
  };

  /**
   * Update intervener GDPR
   * @param {String} uuid UUID of intervener
   * @param {String} uuid_address UUID of address to update
   * @param {Object} payload modified properties
   * @return {Object} API response
   */
  getAddresses = uuid => {
    return this.get(`${ACCOUNTS_API_CONTEXT}/accounts/${uuid}/addresses`);
  };

  /**
   * Update account info
   * @param {String} uuid UUID of account
   * @param {Object} payload modified properties
   * @return {Object} Response data
   */
  updateAccountInfo = (uuid, payload) => {
    return this.patch(`${ACCOUNTS_API_CONTEXT}/accounts/${uuid}`, payload);
  };

  /**
   * Change document objects
   * @param {String} uuid UUID account
   * @param {String} uuidDocuemnt UUID document
   * @param {Object} payload modified properties
   * @return {Object} Response data
   */
  updateDocument = (uuid, uuidDocuemnt, payload) => {
    return this.patch(`${ACCOUNTS_API_CONTEXT}/accounts/${uuid}/documents/${uuidDocuemnt}`, payload);
  };

  /**
   * Remove document
   * @param {String} uuid_account Account UUID
   * @param {String} uuid_document Document UUID
   * @param {Object} config Axios config
   * @return {Object} Response data
   */
  removeDocument = (uuid_account, uuid_document, config) => {
    return this.delete(`${ACCOUNTS_API_CONTEXT}/accounts/${uuid_account}/documents/${uuid_document}`, config);
  };

  /**
   * Delete account
   * @param {String} uuid Account id
   * @return {Object} Response of delete operation
   */
  deleteByUUIId = uuid => {
    return this.delete(`${ACCOUNTS_API_CONTEXT}/accounts/${uuid}`, {});
  };

  /**
   * Change account status
   * @param {String} uuid Account UUID
   * @param {Object} body body of request
   * @return {Object} Response with account changed
   */
  changeAccountStatus = (uuid, body) => {
    return this.post(`${ACCOUNTS_API_CONTEXT}/accounts/${uuid}/status-change`, body);
  };
  /**
   * Get account positions
   * @param {String} uuid account UUID
   * @param {Object} config Axios config
   * @return {Object} Response with account positions data
   */
  getAccountPositions = (uuid, config) => {
    return this.getList(`${ACCOUNTS_API_CONTEXT}/accounts/${uuid}/positions`, config);
  };

  /**
   * Simulate operation
   * @param {Object} payload of the call
   * @return {Object} Response with intervener documents data
   */
  simulateOperation = payload => {
    return this.post(`${ACCOUNTS_API_CONTEXT}/operations/simulation`, payload);
  };

  /**
   * Download all changes in date range
   * @param {String} cfg config file
   * @return {Object} Response with intervener documents data
   */
  downloadAllCrmExportFiles = cfg => {
    const Accept = atob(localStorage.getItem('BOPLCM'));
    Object.assign(cfg || {}, { headers: { Accept }, responseType: 'arraybuffer' });
    return this.get(`${ACCOUNTS_API_CONTEXT}/rd-exports`, cfg).then(response => download(response));
  };

  /**
   * Download all changes in date range
   * @param {String} cfg config file
   * @return {Object} Response with intervener documents data
   */
  getCrmScheduled = cfg => {
    Object.assign(cfg || {}, { headers: { Accept: 'application/json' } });
    return this.get(`${ACCOUNTS_API_CONTEXT}/rd-exports`, cfg);
  };
  accountsMismatchesStatus = cfg => {
    return this.get(`${ACCOUNTS_API_CONTEXT}/accounts-mismatches-status`, cfg);
  };
  accountsMismatch = cfg => {
    return this.get(`${ACCOUNTS_API_CONTEXT}/accounts-mismatches`, cfg);
  };
  accountsKeyProductMismatch = (keyProduct, cfg) => {
    return this.get(`${ACCOUNTS_API_CONTEXT}/accounts-mismatches?product=${keyProduct}`, cfg);
  };
  accountsMismatchSummary = cfg => {
    return this.get(`${ACCOUNTS_API_CONTEXT}/operations-mismatches-summaries`, cfg);
  };
  operationsMismatchesDate = (date, cfg) => {
    return this.get(`${ACCOUNTS_API_CONTEXT}/operations-mismatches?date=${date}`, cfg);
  };
  operationsMismatchesProductAccount = (product_key, account_id, cfg) => {
    return this.get(`${ACCOUNTS_API_CONTEXT}/operations-mismatches?product=${product_key}&account=${account_id}`, cfg);
  };
  createBeneficiaries = (uuid_acccount, payload) => {
    return this.post(`${ACCOUNTS_API_CONTEXT}/accounts/${uuid_acccount}/beneficiaries`, payload);
  };
  /**
   * @param {String} uuid_acccount account UUID
   * @return {Object} Response
   */
  getBeneficiaries = uuid_acccount => {
    return this.getList(`${ACCOUNTS_API_CONTEXT}/accounts/${uuid_acccount}/beneficiaries`);
  };

  /**
   * @param {Object} config Axios config
   * @param {Object} body Axios config
   * @param {String} accountUUID account uuid
   * @param {String} beneficiaryUUID beneficiary uuid
   * @return {Object} Promise
   */
  updateAccountBeneficiary = (config, body, accountUUID, beneficiaryUUID) => {
    return this.patch(`${ACCOUNTS_API_CONTEXT}/accounts/${accountUUID}/beneficiaries/${beneficiaryUUID}`, body, config);
  };

  /**
   * @param {Object} config Axios config
   * @param {String} accountUUID account uuid
   * @param {String} beneficiaryUUID beneficiary uuid
   * @return {Object} Promise
   */
  deleteAccountBeneficiary = (config, accountUUID, beneficiaryUUID) => {
    return this.delete(`accounts-api/accounts/${accountUUID}/beneficiaries/${beneficiaryUUID}`, config);
  };

  /**
   * @param {Object} payload Parametros de la notificación
   * @return {Object} Promise
   */
  createNotification = payload => {
    return this.post(`${ACCOUNTS_API_CONTEXT}/notifications/`, payload);
  };

  /**
   *
   * @param {String} account_uuid UUID
   * @param {Object} document_uuid id Document
   * @return {Object} Promise
   */
  downloadAccountDocument = (account_uuid, document_uuid) => {
    return this.get(`${ACCOUNTS_API_CONTEXT}/accounts/${account_uuid}/documents/${document_uuid}`, {
      responseType: 'blob'
    });
  };
  /**
   *
   * @param {*} uuid_acccount  UUID
   * @return {Object} Promise
   */
  getAccountAssociates = uuid_acccount => {
    return this.get(`${ACCOUNTS_API_CONTEXT}/accounts/${uuid_acccount}/associates`);
  };

  /**
   * Deletes an account associate
   * @param {String} accountUuid Account UUID
   * @param {String} associateUuid Associate UUID
   * @return {Object} Response of deletion
   */
  deleteAccountAssociate = (accountUuid, associateUuid) => {
    return this.delete(`${ACCOUNTS_API_CONTEXT}/accounts/${accountUuid}/associates/${associateUuid}`);
  };

  /**
   *
   * @param {*} uuid_acccount  UUID
   * @return {Object} Promise
   */
  getAccountPatrons = uuid_acccount => {
    return this.get(`${ACCOUNTS_API_CONTEXT}/accounts/${uuid_acccount}/patrons`);
  };

  /**
   * Deletes an account patron
   * @param {String} accountUuid Account UUID
   * @param {String} patronUuid Patron UUID
   * @return {Object} Response of deletion
   */
  deleteAccountPatron = (accountUuid, patronUuid) => {
    return this.delete(`${ACCOUNTS_API_CONTEXT}/accounts/${accountUuid}/patrons/${patronUuid}`);
  };
}

const accountsService = new AccountsService();

export default accountsService;
